import i18n, { translateKey } from '@repo/i18n'
import {
	hashKey,
	type QueryClient,
	type QueryKey,
	type QueryStatus,
	useQueryClient
} from '@tanstack/react-query'
import {
	type AnyRoute,
	Link,
	useMatches,
	useRouter
} from '@tanstack/react-router'
import { HomeIcon } from 'lucide-react'
import { Fragment, useCallback, useMemo, useSyncExternalStore } from 'react'
import {
	Breadcrumb,
	BreadcrumbItem as BreadcrumbItemComponent,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator
} from '#components/breadcrumb'
import { Skeleton } from '#components/skeleton'

type Params = Record<string, string>

/* ---------------------------------- Types --------------------------------- */

/**
 * Type alias for query keys used in breadcrumb cache lookups.
 */
export type QueryKeyType = QueryKey

/**
 * Result from a breadcrumb that includes query state.
 */
export type BreadcrumbFromQueryClient =
	| {
			label: string
			status: QueryStatus
			queryKey: QueryKeyType
	  }
	| {
			label: string
			status: 'success' | 'error'
			queryKey?: never
	  }

/**
 * Helper function to create a breadcrumb from TanStack Query cache.
 * Automatically handles query state, data extraction, and performance optimization.
 *
 * @example
 * ```typescript
 * breadcrumb: ({ params, queryClient }) =>
 *   makeBreadcrumbFromCache<User>(
 *     queryClient,
 *     ['users', params.id],
 *     data => data.name
 *   )
 * ```
 */
export function makeBreadcrumbFromCache<TData>(
	queryClient: QueryClient,
	queryKey: QueryKeyType,
	selector: (data: TData) => string
): BreadcrumbFromQueryClient {
	const state = queryClient.getQueryState<TData>(queryKey)

	if (!state || state.status === 'pending') {
		return {
			label: '',
			status: 'pending',
			queryKey
		}
	}

	if (state.status === 'error') {
		return {
			label: i18n.t('shared:query_breadcrumbs.error'),
			status: 'error',
			queryKey
		}
	}

	if (state.data === undefined) {
		return {
			label: i18n.t('shared:query_breadcrumbs.no_data'),
			status: state.status,
			queryKey
		}
	}

	return {
		label: selector(state.data),
		status: state.status,
		queryKey
	}
}

/**
 * Route handle object expected by the breadcrumb system.
 * Attach this to your route's `staticData` property.
 *
 * @example
 * ```typescript
 * // Static breadcrumb (translation key)
 * { fullPath: '/users', breadcrumb: 'admin-users:page_title' }
 *
 * // Static breadcrumb with count (pluralized key)
 * { fullPath: '/items', breadcrumb: { key: 'shared:async_pickers.selected_count', count: 5 } }
 *
 * // Dynamic breadcrumb from query cache
 * {
 *   fullPath: '/users/:id',
 *   breadcrumb: ({ params, queryClient }) =>
 *     makeBreadcrumbFromCache(queryClient, ['users', params.id], data => data.name)
 * }
 * ```
 */
interface HandleRouterObj {
	/** Canonical destination when the layout path is a legacy alias. */
	breadcrumbTo?: string
	breadcrumb?:
		| string
		| { key: string; count?: number }
		| ((ctx: {
				params: Params
				queryClient: QueryClient
		  }) => BreadcrumbFromQueryClient)
}

interface BreadcrumbItem {
	label: string
	to: string
	isCurrent: boolean
	status: QueryStatus
	linkable: boolean
}

/* --------------------------------- Helpers -------------------------------- */

/**
 * Whether a crumb's own path is a meaningful destination worth linking.
 * An index child shares the layout's `to`, so its presence means the layout
 * path renders a real page (e.g. a list) and the crumb should link there.
 * Without an index child, a layout that has exactly one real sub-route only
 * redirects to it, so its own path isn't a destination and shouldn't link.
 * Derived from the route tree, so it stays correct as routes are added or
 * removed.
 */
function isLinkableCrumbRoute(route: AnyRoute | undefined): boolean {
	const children: AnyRoute[] = route?.children ?? []
	const hasIndexChild = children.some((child) => child.to === route?.to)
	if (hasIndexChild) {
		return true
	}
	const subRoutes = children.filter((child) => child.to !== route?.to)
	return subRoutes.length !== 1
}

function isHandleRouterObj(handle: unknown): handle is HandleRouterObj {
	if (!handle || typeof handle !== 'object') {
		return false
	}

	const hasValidBreadcrumb =
		!('breadcrumb' in handle) ||
		typeof handle.breadcrumb === 'string' ||
		typeof handle.breadcrumb === 'function' ||
		(typeof handle.breadcrumb === 'object' &&
			handle.breadcrumb !== null &&
			'key' in handle.breadcrumb &&
			typeof handle.breadcrumb.key === 'string')

	return (
		hasValidBreadcrumb &&
		(!('breadcrumbTo' in handle) ||
			handle.breadcrumbTo === undefined ||
			typeof handle.breadcrumbTo === 'string')
	)
}

function resolveHandleBreadcrumb(
	handle: HandleRouterObj,
	params: Params,
	queryClient: QueryClient
): BreadcrumbFromQueryClient | null {
	if (!handle.breadcrumb) {
		return null
	}

	if (typeof handle.breadcrumb === 'string') {
		return { label: translateKey(handle.breadcrumb), status: 'success' }
	}

	if (
		typeof handle.breadcrumb === 'object' &&
		handle.breadcrumb !== null &&
		'key' in handle.breadcrumb
	) {
		const translationKey = handle.breadcrumb.key
		const count = handle.breadcrumb.count

		if (count !== undefined) {
			return {
				label: translateKey(translationKey, { count }),
				status: 'success'
			}
		}

		return { label: translateKey(translationKey), status: 'success' }
	}

	try {
		return handle.breadcrumb({ params, queryClient })
	} catch {
		return {
			label: i18n.t('shared:query_breadcrumbs.error'),
			status: 'error'
		}
	}
}

/* ------------------------------ Components -------------------------------- */

function QueryBreadcrumbs() {
	const matches = useMatches()
	const queryClient = useQueryClient()
	const router = useRouter()

	const validMatches = useMemo(
		() =>
			matches.filter(
				(
					match
				): match is typeof match & { staticData: HandleRouterObj } =>
					isHandleRouterObj(match.staticData)
			),
		[matches]
	)

	const relevantQueryKeys = useMemo(() => {
		const keys: QueryKeyType[] = []

		for (const match of validMatches) {
			const breadcrumb = match.staticData.breadcrumb
			if (typeof breadcrumb !== 'function') {
				continue
			}

			try {
				const result = breadcrumb({
					params: match.params ?? {},
					queryClient
				})
				if (result.queryKey) {
					keys.push(result.queryKey)
				}
			} catch {
				// Swallow errors here; Phase 2 re-invokes and surfaces errors.
			}
		}

		return keys
	}, [validMatches, queryClient])

	const relevantHashes = useMemo(
		() => new Set(relevantQueryKeys.map((key) => hashKey(key))),
		[relevantQueryKeys]
	)

	const subscribe = useCallback(
		(callback: () => void) =>
			queryClient.getQueryCache().subscribe((event) => {
				if (!event?.query) {
					return
				}

				if (relevantHashes.has(hashKey(event.query.queryKey))) {
					callback()
				}
			}),
		[queryClient, relevantHashes]
	)

	const getSnapshot = useCallback(() => {
		if (relevantQueryKeys.length === 0) {
			return '0'
		}

		let maxTimestamp = 0

		for (const queryKey of relevantQueryKeys) {
			const state = queryClient.getQueryState(queryKey)

			if (!state) {
				continue
			}

			const timestamp = state.data
				? (state.dataUpdatedAt ?? 0)
				: // biome-ignore lint/style/noNestedTernary: more readable than if-else here
					state.status === 'error'
					? (state.errorUpdatedAt ?? 0)
					: 0

			if (timestamp > maxTimestamp) {
				maxTimestamp = timestamp
			}
		}

		return maxTimestamp.toString()
	}, [queryClient, relevantQueryKeys])

	useSyncExternalStore(subscribe, getSnapshot)

	const breadcrumbs: BreadcrumbItem[] = validMatches
		.map((match) => {
			const resolved = resolveHandleBreadcrumb(
				match.staticData,
				match.params ?? {},
				queryClient
			)

			if (!resolved) {
				return null
			}

			return {
				label: resolved.label,
				status: resolved.status,
				to: match.staticData.breadcrumbTo ?? match.pathname,
				isCurrent: false,
				linkable:
					match.staticData.breadcrumbTo !== undefined ||
					isLinkableCrumbRoute(router.routesById[match.routeId])
			}
		})
		.filter((crumb): crumb is BreadcrumbItem => crumb !== null)
		.map((crumb, index, all) => ({
			...crumb,
			isCurrent: index === all.length - 1
		}))

	return (
		<Breadcrumb>
			<BreadcrumbList>
				<BreadcrumbItemComponent>
					<BreadcrumbLink render={<Link aria-label='Home' to='/' />}>
						<HomeIcon className='size-4' />
					</BreadcrumbLink>
				</BreadcrumbItemComponent>
				{breadcrumbs.length > 0 && <BreadcrumbSeparator />}
				{breadcrumbs.map((crumb, index) => (
					<Fragment key={`${crumb.to}-${index}`}>
						<QueryBreadcrumbItem breadcrumb={crumb} />
						{index < breadcrumbs.length - 1 && (
							<BreadcrumbSeparator />
						)}
					</Fragment>
				))}
			</BreadcrumbList>
		</Breadcrumb>
	)
}

function QueryBreadcrumbItem({ breadcrumb }: { breadcrumb: BreadcrumbItem }) {
	if (breadcrumb.status === 'pending') {
		return (
			<BreadcrumbItemComponent>
				<Skeleton className='h-4 w-20' />
			</BreadcrumbItemComponent>
		)
	}

	if (breadcrumb.status === 'error') {
		return (
			<BreadcrumbItemComponent className='text-destructive'>
				{breadcrumb.label}
			</BreadcrumbItemComponent>
		)
	}

	if (breadcrumb.isCurrent) {
		return (
			<BreadcrumbItemComponent>
				<BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>
			</BreadcrumbItemComponent>
		)
	}

	if (!breadcrumb.linkable) {
		return (
			<BreadcrumbItemComponent className='text-muted-foreground'>
				{breadcrumb.label}
			</BreadcrumbItemComponent>
		)
	}

	return (
		<BreadcrumbItemComponent>
			<BreadcrumbLink render={<Link to={breadcrumb.to} />}>
				{breadcrumb.label}
			</BreadcrumbLink>
		</BreadcrumbItemComponent>
	)
}

export { QueryBreadcrumbs }
